// @truco6/planos: a matriz única de permissões por plano (CLAUDE.md, "Planos e permissões").
// O servidor valida toda ação contra ela; o aparelho só mostra ou esconde o cadeado.
// Funcionalidade nova entra aqui como uma linha, ligada a um plano, e na tabela do CLAUDE.md.

export const PLANOS = ['gratis', 'bronze', 'prata', 'ouro'] as const;
export type Plano = (typeof PLANOS)[number];

export const NOME_DO_PLANO: Record<Plano, string> = { gratis: 'Grátis', bronze: 'Bronze', prata: 'Prata', ouro: 'Ouro' };

type Quatro<T> = readonly [T, T, T, T];

export interface Linha<V> {
  nome: string; // primeira coluna da tabela
  celulas: Record<Plano, string>; // o texto de cada célula, como está no CLAUDE.md
  valores: Record<Plano, V>; // o que o código usa
}

function linha<V>(nome: string, celulas: Quatro<string>, valores: Quatro<V>): Linha<V> {
  const c = {} as Record<Plano, string>;
  const v = {} as Record<Plano, V>;
  PLANOS.forEach((p, i) => { c[p] = celulas[i]!; v[p] = valores[i]!; });
  return { nome, celulas: c, valores: v };
}

// Estatísticas e cosméticos guardam o nível; os demais são sim ou não, ou um número (null = ilimitado).
export type NivelEstatisticas = 'basicas' | 'completas' | 'historico';
// Trios fixos: quantos (contando os de amigos), se cria, se o perfil mostra o histórico e se mostra todos os trios.
export interface TriosFixos { limite: number; cria: boolean; historico: boolean; todosNoPerfil: boolean }
export type Cosmeticos = number | 'todos';

// A ordem das linhas segue a tabela do CLAUDE.md.
export const MATRIZ = {
  partidasPorDia: linha<number | null>('Partidas por dia',
    ['2, em qualquer mesa', 'Ilimitadas', 'Ilimitadas', 'Ilimitadas'], [2, null, null, null]),
  jogarEmSalaDeAmigo: linha<boolean>('Jogar em sala de amigo',
    ['Sim, conta no limite', 'Sim', 'Sim', 'Sim'], [true, true, true, true]),
  amigos: linha<boolean>('Amigos e filtro de salas com amigos',
    ['Sim', 'Sim', 'Sim', 'Sim'], [true, true, true, true]),
  bonusPorIndicacao: linha<number>('Bônus por amigo que criar conta',
    ['+5 partidas', '', '', ''], [5, 0, 0, 0]),
  partidaExtraPorAnuncio: linha<boolean>('Partida extra por anúncio',
    ['Sim', '', '', ''], [true, false, false, false]),
  anuncioEntrePartidas: linha<boolean>('Anúncio entre partidas',
    ['Sim', 'Não', 'Não', 'Não'], [true, false, false, false]),
  trioFixo: linha<boolean>('Trio fixo com nome e escudo',
    ['Não', 'Sim', 'Sim', 'Sim'], [false, true, true, true]),
  triosFixos: linha<TriosFixos>('Trios fixos',
    ['Entra em 1 trio de amigo, não cria, sem histórico', '1, só nome e escudo, sem histórico', 'Até 3, com histórico de cada trio', 'Até 10, com histórico de cada trio, todos no perfil'],
    [
      { limite: 1, cria: false, historico: false, todosNoPerfil: false },
      { limite: 1, cria: true, historico: false, todosNoPerfil: false },
      { limite: 3, cria: true, historico: true, todosNoPerfil: false },
      { limite: 10, cria: true, historico: true, todosNoPerfil: true },
    ]),
  estatisticas: linha<NivelEstatisticas>('Estatísticas',
    ['Básicas', 'Completas', 'Completas', 'Completas com histórico'], ['basicas', 'completas', 'completas', 'historico']),
  // Regras da casa: tempo por jogada, sinais, zoeira e vagas com bot. Toda sala é pública; sem o plano, a mesa recusa
  // qualquer regra fora do padrão, e a sala sai com as regras padrão.
  regrasDaCasa: linha<boolean>('Regras da casa na sala',
    ['Não', 'Não', 'Sim', 'Sim'], [false, false, true, true]),
  marcadorPresencial: linha<boolean>('Marcador presencial',
    ['Não', 'Não', 'Sim', 'Sim'], [false, false, true, true]),
  assistirSalaPublica: linha<boolean>('Assistir sala pública',
    ['Sim', 'Sim', 'Sim', 'Sim'], [true, true, true, true]),
  criarLiga: linha<boolean>('Criar liga, comunidade e campeonato',
    ['Não', 'Não', 'Não', 'Sim'], [false, false, false, true]),
  entrarEmComunidade: linha<boolean>('Entrar em comunidade e jogar nas mesas dela',
    ['Sim', 'Sim', 'Sim', 'Sim'], [true, true, true, true]),
  jogarCampeonato: linha<boolean>('Inscrever o trio fixo e jogar campeonato',
    ['Sim', 'Sim', 'Sim', 'Sim'], [true, true, true, true]),
  ferramentasDeOrganizador: linha<boolean>('Ferramentas de organizador',
    ['Não', 'Não', 'Não', 'Sim'], [false, false, false, true]),
  cosmeticos: linha<Cosmeticos>('Cosméticos (extras comprados com Tentos)',
    ['1 de cada', '+3', '+6', 'Coleção completa'], [1, 3, 6, 'todos']),
  gravarGritos: linha<boolean>('Gravar os próprios gritos',
    ['Não', 'Não', 'Sim', 'Sim'], [false, false, true, true]),
  tentosPorMes: linha<number>('Tentos por mês do plano',
    ['0', '0', '20', '50'], [0, 0, 20, 50]),
  comprarTentos: linha<boolean>('Comprar pacotes de Tentos',
    ['Sim', 'Sim', 'Sim', 'Sim'], [true, true, true, true]),
  criarSalaComTentos: linha<boolean>('Criar sala valendo Tentos',
    ['Não', 'Não', 'Sim', 'Sim'], [false, false, true, true]),
  entrarEmSalaComTentos: linha<boolean>('Entrar em sala valendo Tentos',
    ['Sim, com saldo', 'Sim, com saldo', 'Sim, com saldo', 'Sim, com saldo'], [true, true, true, true]),
} as const;

export type Recurso = keyof typeof MATRIZ;
export type RecursoSimNao = {
  [K in Recurso]: (typeof MATRIZ)[K] extends Linha<boolean> ? K : never;
}[Recurso];

export function ehPlano(x: unknown): x is Plano {
  return typeof x === 'string' && (PLANOS as readonly string[]).includes(x);
}

export function pode(plano: Plano | null | undefined, recurso: RecursoSimNao): boolean {
  return !!plano && MATRIZ[recurso].valores[plano];
}

// Partidas grátis por dia, em qualquer mesa; null é ilimitado.
export function limiteDiario(plano: Plano): number | null {
  return MATRIZ.partidasPorDia.valores[plano];
}

// Partidas que quem indica ganha quando o amigo cria conta (vale para qualquer plano; só o Grátis precisa).
export const BONUS_POR_INDICACAO = MATRIZ.bonusPorIndicacao.valores.gratis;

// "Prata e Ouro", para o convite de troca de plano no cadeado.
export function planosQueTem(recurso: RecursoSimNao): string {
  const nomes = PLANOS.filter((p) => pode(p, recurso)).map((p) => NOME_DO_PLANO[p]);
  return nomes.length > 1 ? nomes.slice(0, -1).join(', ') + ' e ' + nomes[nomes.length - 1] : nomes[0] ?? '';
}

// Quanto maior, mais completo. Serve para escolher o maior plano vigente.
export function ordemDoPlano(p: Plano): number {
  return PLANOS.indexOf(p);
}

export function maiorPlano(planos: readonly Plano[]): Plano {
  return planos.reduce<Plano>((a, b) => (ordemDoPlano(b) > ordemDoPlano(a) ? b : a), 'gratis');
}

// ---------- cobrança ----------
export type PlanoPago = Exclude<Plano, 'gratis'>;
export const PLANOS_PAGOS: readonly PlanoPago[] = ['bronze', 'prata', 'ouro'];

// Ciclo de cobrança. O anual é cobrado à vista, uma vez por ano, com 4 mensalidades de desconto.
export const CICLOS = ['mensal', 'anual'] as const;
export type Ciclo = (typeof CICLOS)[number];
export const NOME_DO_CICLO: Record<Ciclo, string> = { mensal: 'Mensal', anual: 'Anual' };
export const MESES_GRATIS_NO_ANUAL = 4;

export function ehCiclo(x: unknown): x is Ciclo {
  return typeof x === 'string' && (CICLOS as readonly string[]).includes(x);
}

// Preço em centavos de real. O anual tem parcela redonda: 12 vezes a parcela fecha o total, e a economia
// fica em 4 mensalidades ou um pouco mais (R$ 1,32, R$ 6,66 e R$ 19,99 por mês).
export const PRECO_CENTAVOS: Record<PlanoPago, number> = { bronze: 199, prata: 999, ouro: 2999 };
export const PRECO_ANUAL_CENTAVOS: Record<PlanoPago, number> = { bronze: 1584, prata: 7992, ouro: 23988 };

// Lookup key de cada preço na Stripe: 'prata_mensal', 'prata_anual'...
export const LOOKUP_KEY: Record<PlanoPago, Record<Ciclo, string>> = {
  bronze: { mensal: 'bronze_mensal', anual: 'bronze_anual' },
  prata: { mensal: 'prata_mensal', anual: 'prata_anual' },
  ouro: { mensal: 'ouro_mensal', anual: 'ouro_anual' },
};

export function precoCentavos(p: PlanoPago, ciclo: Ciclo): number {
  return ciclo === 'anual' ? PRECO_ANUAL_CENTAVOS[p] : PRECO_CENTAVOS[p];
}

// Quanto o anual sai por mês, para comparar com o mensal.
export function parcelaDoAnual(p: PlanoPago): number {
  return PRECO_ANUAL_CENTAVOS[p] / 12;
}

// Quanto o anual economiza em relação a 12 mensalidades.
export function economiaNoAnual(p: PlanoPago): number {
  return 12 * PRECO_CENTAVOS[p] - PRECO_ANUAL_CENTAVOS[p];
}

export function emReais(centavos: number): string {
  return 'R$ ' + (Math.round(centavos) / 100).toFixed(2).replace('.', ',');
}

// Os três planos pagos estão à venda desde a Liga da Semana.
export const A_VENDA: readonly PlanoPago[] = ['bronze', 'prata', 'ouro'];

export function ehPlanoPago(x: unknown): x is PlanoPago {
  return ehPlano(x) && x !== 'gratis';
}

export function precoEmReais(p: PlanoPago, ciclo: Ciclo = 'mensal'): string {
  return emReais(precoCentavos(p, ciclo));
}

// Recursos que ainda não existem no jogo. Aparecem com o selo "Em breve" e nunca entram em "Liberou agora".
// A coleção e a loja de cosméticos entraram no lote 3 e saíram daqui; as estatísticas por plano e o marcador presencial,
// no lote 6; as comunidades, os campeonatos e as ferramentas de organizador, no lote 7.
export const EM_BREVE: ReadonlySet<Recurso> = new Set<Recurso>([
  'partidaExtraPorAnuncio', 'anuncioEntrePartidas',
]);

// Até a Liga da Semana, qualquer conta criava sala privada. Com a liga no ar (etapa 5), as regras da casa seguem
// `regrasDaCasa` como na tabela. Toda sala nova é pública, para qualquer conta.
export const SALA_LIVRE_ATE_A_LIGA = false;

// Liga da Semana no jogo: desligada, somem o JOGAR NA LIGA, a busca de mesa, a tela da liga e o patrocinador. Ranking,
// pontos do ranking, PD, divisões, temporada, fechamento da semana e badges regionais seguem, porque contam todas as
// salas. Ligar de novo traz tudo de volta, sem migração.
export const LIGA_DA_SEMANA = false;

// Assinatura à vista (item 59): onde quem está no Grátis viu o aviso do limite e de onde tocou em ASSINAR ou em Chamar
// um amigo. Viram os eventos limite_aviso_visto, assinar_toque e indique_toque do painel.
// O guia do dia (item 67) entra como 'entrada'; o cartão "Só resta 1" do fim de partida e a linha da revanche (item 68),
// como 'fim_resta_uma' e 'revanche'. Os toques do guia têm a origem 'guia'.
export const ONDE_DO_AVISO_DO_LIMITE = ['selo', 'mesa_chip', 'mesa_faixa', 'fim_partida', 'limite', 'entrada', 'fim_resta_uma', 'revanche'] as const;
export type OndeDoAvisoDoLimite = (typeof ONDE_DO_AVISO_DO_LIMITE)[number];
export const ORIGENS_DO_TOQUE = ['menu', 'selo', 'mesa', 'fim_partida', 'limite', 'perfil', 'planos', 'guia'] as const;
export type OrigemDoToque = (typeof ORIGENS_DO_TOQUE)[number];

// Página "Você já jogou suas 2 partidas grátis de hoje" (item 69): a vantagem principal de cada plano pago, com a
// herança do plano de baixo e as linhas da matriz que ela promete. O teste confere que o plano tem cada linha, que o
// plano de baixo não tem e que nenhuma está em EM_BREVE. O Prata fala em regras da casa porque qualquer conta já cria
// sala com as regras padrão.
export interface VantagemPrincipal { heranca: string; titulo: string; detalhe: string; recursos: readonly Recurso[] }
export const VANTAGEM_PRINCIPAL: Record<PlanoPago, VantagemPrincipal> = {
  bronze: { heranca: 'Para começar', titulo: 'Jogue sem limite', detalhe: 'Partidas ilimitadas em qualquer mesa', recursos: ['partidasPorDia'] },
  prata: { heranca: 'Tudo do Bronze +', titulo: 'Crie sala com suas regras', detalhe: 'Regras da casa e sala valendo Tentos', recursos: ['regrasDaCasa', 'criarSalaComTentos'] },
  ouro: { heranca: 'Tudo do Prata +', titulo: 'Crie campeonatos e comunidades', detalhe: 'Organize a sua turma', recursos: ['criarLiga'] },
};

// Dias que o Pix tem para ser pago, e a tolerância do plano enquanto a renovação está em aberto.
export const DIAS_PARA_PAGAR_PIX = 3;
export const DIAS_DE_TOLERANCIA = 3;

// Prazo para desistir com o valor de volta (CDC, art. 49).
export const DIAS_PARA_DESISTIR = 7;

// A indicação vale para conta Google criada há até estes dias.
export const DIAS_PARA_ACEITAR_INDICACAO = 7;

// ---------- Amigos ----------
// Amizade só entre contas Google, em qualquer plano. O dia dos pedidos é o de Brasília.
export const AMIGOS_MAX = 2000;
export const PEDIDOS_AMIZADE_POR_DIA = 30;
// O pedido vence nestes dias; depois de recusado ou vencido, um novo pedido para a mesma pessoa espera o mesmo tanto.
export const DIAS_PEDIDO_AMIZADE = 30;
// Convites de trio feitos por conta, por dia, e os dias até o convite vencer.
export const CONVITES_TRIO_POR_DIA = 10;
export const DIAS_CONVITE_TRIO = 7;

// ---------- Celular da conta ----------
// Números novos por hora por conta, contando os recusados: ninguém descobre quais números têm conta tentando vários.
export const TENTATIVAS_CELULAR_POR_HORA = 5;

// ---------- Comunidades (o Ouro cria; qualquer conta Google entra) ----------
export const COMUNIDADES_POR_DONO = 5;
export const MEMBROS_DA_COMUNIDADE_MAX = 500;
export const MODERADORES_MAX = 5;
export const NOME_DA_COMUNIDADE_MAX = 40; // caracteres
export const DESCRICAO_DA_COMUNIDADE_MAX = 280; // caracteres
export const POST_MAX = 500; // caracteres
export const IMAGEM_DO_POST_MAX_BYTES = 1_000_000; // JPEG, PNG ou WebP
export const DIAS_SEM_OURO = 90; // sem dono Ouro por esse tempo, a comunidade fica só para leitura e mesas

// ---------- Notificações no celular ----------
export const PUSH_POR_DIA = 5; // por conta, somando todos os tipos
export const OFENSIVA_POR_DIA = 2; // lembretes da ofensiva
export const SILENCIO_DO_PUSH = { de: 23, ate: 9 } as const; // horas em Brasília; só o chamado para a mesa fura

// ---------- Tentos ----------
// Tentos não têm valor em dinheiro: sem saque, troca ou transferência. O servidor valida tudo contra estas constantes.
export const TENTOS_POR_MES: Readonly<Record<Plano, number>> = MATRIZ.tentosPorMes.valores;

export type ChavePacote = 'tentos_10' | 'tentos_30' | 'tentos_75' | 'tentos_160';
export interface PacoteTentos { chave: ChavePacote; tentos: number; centavos: number }
export const PACOTES_TENTOS: readonly PacoteTentos[] = [
  { chave: 'tentos_10', tentos: 10, centavos: 499 },
  { chave: 'tentos_30', tentos: 30, centavos: 1299 },
  { chave: 'tentos_75', tentos: 75, centavos: 2799 },
  { chave: 'tentos_160', tentos: 160, centavos: 4999 },
];
export function pacoteDe(chave: unknown): PacoteTentos | null {
  return PACOTES_TENTOS.find((p) => p.chave === chave) ?? null;
}
// Desconto do pacote sobre o preço por Tento do menor, em %, arredondado para baixo (13, 25, 37).
export function descontoDoPacote(p: PacoteTentos): number {
  const base = PACOTES_TENTOS[0]!.centavos / PACOTES_TENTOS[0]!.tentos;
  return Math.floor((1 - p.centavos / p.tentos / base) * 100);
}
export const TETO_COMPRA_TENTOS_MES_CENTAVOS = 15_000; // por mês civil e por conta
// Página de pagamento e Pix da compra valem 1 hora; a página do cartão da assinatura também.
export const VALIDADE_DA_COMPRA_MIN = 60;
// Desistência de um pacote de Tentos, pelo extrato.
export const DIAS_PARA_DESISTIR_DA_COMPRA = 7;

// Ingresso por jogador da sala valendo Tentos. O teto antifraude é o maior valor da lista.
export const INGRESSOS_TENTOS = [3, 6, 9, 15, 30] as const;
export const TETO_INGRESSO_TENTOS = 30;
export function ehIngresso(x: unknown): x is (typeof INGRESSOS_TENTOS)[number] {
  return typeof x === 'number' && (INGRESSOS_TENTOS as readonly number[]).includes(x);
}

// Partida aberta há mais que isso (a mesa caiu e não voltou) é anulada e devolve os ingressos.
export const HORAS_PARA_ANULAR_PARTIDA = 6;

// ---------- salas ----------
export const NOME_DE_SALA_MAX = 30; // caracteres
export const DENUNCIAS_PARA_OCULTAR = 3; // contas diferentes; a sala sai da lista e vai para a revisão
export const PRAZO_REVANCHE_S = 30; // cada jogador confirma a revanche nesse prazo

export interface Pote {
  pote: number; // ingresso × 6
  casa: number; // o valor de um jogador, queimado
  premioPorVencedor: number; // parte inteira de ingresso × 5 ÷ 3
  premioPago: number; // prêmio × vencedores que recebem
  queima: number; // casa + resto da divisão + a parte de quem saiu
}
// Pote da sala valendo Tentos, sempre em inteiros: prêmio pago + queima = pote.
// vencedoresPresentes: os do trio campeão que recebem (quem saiu da partida não recebe).
export function calcularPote(ingresso: number, vencedoresPresentes = 3): Pote {
  if (!Number.isInteger(ingresso) || ingresso <= 0) throw new Error('ingresso inválido');
  if (!Number.isInteger(vencedoresPresentes) || vencedoresPresentes < 0 || vencedoresPresentes > 3) throw new Error('vencedores inválidos');
  const pote = ingresso * 6, premioPorVencedor = Math.floor((ingresso * 5) / 3), premioPago = premioPorVencedor * vencedoresPresentes;
  return { pote, casa: ingresso, premioPorVencedor, premioPago, queima: pote - premioPago };
}

// ---------- textos para as telas de plano ----------
export interface ItemDoPlano {
  recurso: Recurso;
  texto: string;
  emBreve: boolean;
}

const TRIOS_FIXOS: Record<Plano, string | null> = {
  gratis: 'Entrar em 1 trio fixo de amigo', bronze: null, prata: 'Até 3 trios fixos, com histórico', ouro: 'Até 10 trios fixos, com histórico no perfil',
};
const COSMETICOS: Record<string, string> = { 1: '1 cosmético de cada', 3: '+3 cosméticos', 6: '+6 cosméticos', todos: 'Coleção completa de cosméticos' };
const ESTATISTICAS: Record<NivelEstatisticas, string> = {
  basicas: 'Estatísticas básicas', completas: 'Estatísticas completas', historico: 'Estatísticas completas com histórico',
};

// O que o plano tem, na ordem da matriz, em frase de tela. Linhas que o plano não tem ficam de fora.
export function itensDoPlano(plano: Plano): ItemDoPlano[] {
  const v = (r: RecursoSimNao) => MATRIZ[r].valores[plano];
  const liga = MATRIZ.partidasPorDia.valores[plano], bonus = MATRIZ.bonusPorIndicacao.valores[plano];
  const textos: [Recurso, string | null][] = [
    ['partidasPorDia', liga === null ? 'Partidas ilimitadas em qualquer mesa' : `${liga} partidas por dia, em qualquer mesa`],
    ['jogarEmSalaDeAmigo', 'Jogar em sala de amigo'],
    ['bonusPorIndicacao', bonus > 0 ? `+${bonus} partidas por amigo que criar conta` : null],
    ['partidaExtraPorAnuncio', v('partidaExtraPorAnuncio') ? 'Partida extra por anúncio' : null],
    ['anuncioEntrePartidas', v('anuncioEntrePartidas') ? null : 'Sem anúncio entre partidas'],
    ['trioFixo', v('trioFixo') ? 'Trio fixo com nome e escudo' : null],
    ['triosFixos', TRIOS_FIXOS[plano]],
    ['estatisticas', ESTATISTICAS[MATRIZ.estatisticas.valores[plano]]],
    ['regrasDaCasa', v('regrasDaCasa') ? 'Criar sala com regras da casa' : 'Criar sala com as regras padrão'],
    ['marcadorPresencial', v('marcadorPresencial') ? 'Marcador presencial com placar ao vivo' : null],
    ['criarLiga', v('criarLiga') ? 'Criar liga, comunidade e campeonato' : null],
    ['ferramentasDeOrganizador', v('ferramentasDeOrganizador') ? 'Ferramentas de organizador' : null],
    ['cosmeticos', COSMETICOS[String(MATRIZ.cosmeticos.valores[plano])] ?? null],
    ['gravarGritos', v('gravarGritos') ? 'Gravar os próprios gritos' : null],
    ['tentosPorMes', MATRIZ.tentosPorMes.valores[plano] > 0 ? `${MATRIZ.tentosPorMes.valores[plano]} Tentos por mês` : null],
    ['criarSalaComTentos', v('criarSalaComTentos') ? 'Criar sala valendo Tentos' : null],
  ];
  return textos.flatMap(([recurso, texto]) => {
    if (!texto) return [];
    // "Sem anúncio" já vale hoje: o jogo ainda não tem anúncio nenhum.
    const emBreve = recurso === 'anuncioEntrePartidas' ? false : EM_BREVE.has(recurso);
    return [{ recurso, texto, emBreve }];
  });
}

// O cartão curto do plano (site e tela de planos): o Grátis lista o que tem e cada plano pago, o que acrescenta ao
// plano de baixo ("Tudo do Bronze, e:"), na ordem do que mais pesa na escolha e com até ITENS_NO_CARTAO itens. A
// comparação completa fica na tabela da matriz.
export const ITENS_NO_CARTAO = 6;
const PESO_NO_CARTAO: Recurso[] = [
  'partidasPorDia', 'regrasDaCasa', 'criarLiga', 'ferramentasDeOrganizador', 'tentosPorMes', 'criarSalaComTentos',
  'triosFixos', 'trioFixo', 'anuncioEntrePartidas', 'estatisticas', 'marcadorPresencial',
  'jogarEmSalaDeAmigo', 'bonusPorIndicacao', 'partidaExtraPorAnuncio', 'gravarGritos', 'cosmeticos',
];

export function planoDeBaixo(plano: Plano): Plano | null {
  const i = PLANOS.indexOf(plano);
  return i > 0 ? PLANOS[i - 1]! : null;
}

export function itensNovos(plano: Plano): ItemDoPlano[] {
  const abaixo = planoDeBaixo(plano);
  const mudou = (r: Recurso) => !abaixo || JSON.stringify(MATRIZ[r].valores[plano]) !== JSON.stringify(MATRIZ[r].valores[abaixo]);
  const peso = (r: Recurso) => { const i = PESO_NO_CARTAO.indexOf(r); return i < 0 ? PESO_NO_CARTAO.length : i; };
  return itensDoPlano(plano).filter((i) => mudou(i.recurso)).sort((a, b) => peso(a.recurso) - peso(b.recurso)).slice(0, ITENS_NO_CARTAO);
}

// O que o plano libera a mais que o Grátis e já se sente no jogo: a lista "Liberou agora".
// "Sem anúncio" fica de fora enquanto o Grátis também não tem anúncio.
export function liberouAgora(plano: Plano): ItemDoPlano[] {
  return itensDoPlano(plano).filter((i) => !i.emBreve && i.recurso !== 'anuncioEntrePartidas'
    && MATRIZ[i.recurso].valores[plano] !== MATRIZ[i.recurso].valores.gratis);
}
